import React from "react";
import { FormikProps } from "formik";
import * as Yup from "yup";
import { QuestionDto } from "../../../services/dto";
import Drawer from "../../../assets/vfx/drawer/drawer";
import Form from "../../../assets/vfx/form/form";
import { Constants } from "../../../assets/utils/lib/constants";
import { StringHelper } from "../../../assets/utils/helpers";
import QuestionOption from "./questionOption";
import { DropdownOption } from "../../../assets/vfx/controls/components/dropdown/controlDropdown";
import { QuestionnaireType } from "../questionnaireForm/questionnaireContainer";
import { DrawerFormSaveResult, hasClientErrorOperationStatusCode } from "../../../assets/utils/lib";

export const QuestionTypes = {
    Date: "68719DF0-15B6-EF11-B244-9CABC6BFEE6B",
    Time: "69719DF0-15B6-EF11-B244-9CABC6BFEE6B",
    DateTime: "6A719DF0-15B6-EF11-B244-9CABC6BFEE6B",
    Text: "6B719DF0-15B6-EF11-B244-9CABC6BFEE6B",
    Checkbox: "6C719DF0-15B6-EF11-B244-9CABC6BFEE6B",
    Radio: "6D719DF0-15B6-EF11-B244-9CABC6BFEE6B",
    Dropdown: "6E719DF0-15B6-EF11-B244-9CABC6BFEE6B",
    MultiLineTextbox: "6F719DF0-15B6-EF11-B244-9CABC6BFEE6B",
    Numerical: "77f9961f-f26e-f011-8ef6-00155d112802"
};

export interface QuestionLookupProps {
    questionType: DropdownOption[];
    sections: DropdownOption[];
}

interface QuestionnaireQuestionFormProps {
    data?: QuestionDto | undefined;
    show: boolean;
    questionnaireId?: string | undefined;
    lookups: QuestionLookupProps | undefined;
    onHide: () => void;
    onSave: (values: QuestionDto) => DrawerFormSaveResult;
    questionnaireType?: string | undefined;
}

const validationSchema = Yup.object().shape({
    name: Yup.string().required("Question is required"),
    description: Yup.string().required("Description is required"),

    sectionId: Yup.string().required("Section is required"),
    questionTypeId: Yup.string().required("Question Type is required"),
    questionOrder: Yup.number()
        .min(1, "Order must be at least 1")
        .required("Order is required"),
    options: Yup.array().of(
        Yup.object().shape({
            name: Yup.string().required("Option is required"),
        })
    ),
    rangeMinValue: Yup.mixed().when("questionTypeId", {
        is: (val: string) =>
            val !== QuestionTypes.Text.toLowerCase() &&
            val !== QuestionTypes.MultiLineTextbox.toLowerCase() &&
            val !== QuestionTypes.Checkbox.toLowerCase() &&
            val !== QuestionTypes.Radio.toLowerCase() &&
            val !== QuestionTypes.Dropdown.toLowerCase(),
        then: () => Yup.mixed().required("Min Value is required."),
        otherwise: () => Yup.mixed().notRequired(),
    }),

    rangeMaxValue: Yup.mixed().when(["questionTypeId", "rangeMinValue"], {
        is: (questionTypeId: string, rangeMinValue: any) =>
            questionTypeId !== QuestionTypes.Text.toLowerCase() &&
            questionTypeId !== QuestionTypes.MultiLineTextbox.toLowerCase() &&
            questionTypeId !== QuestionTypes.Checkbox.toLowerCase() &&
            questionTypeId !== QuestionTypes.Radio.toLowerCase() &&
            questionTypeId !== QuestionTypes.Dropdown.toLowerCase() &&
            rangeMinValue != null,
        then: () =>
            Yup.mixed()
                .test(
                    "is-greater",
                    "Max Value must be greater than Min Value",
                    function (rangeMaxValue) {
                        return (
                            rangeMaxValue !== undefined &&
                            rangeMaxValue > this.parent.rangeMinValue
                        );
                    }
                )
                .required("Max Value is required."),
        otherwise: () => Yup.mixed().notRequired(),
    }),
});

export const QuestionForm = (props: QuestionnaireQuestionFormProps) => {
    const initialValues: QuestionDto = {
        id: props?.data?.id ?? Constants.emptyGuid,
        questionTypeId: props?.data?.questionTypeId ?? "",
        sectionId: props?.data?.sectionId ?? "",
        name: props?.data?.name ?? "",
        description: props?.data?.description ?? "",
        isRequired: props?.data?.isRequired ?? false,
        questionOrder: props?.data?.questionOrder ?? 1,
        isDeleted: props?.data?.isDeleted ?? false,
        rangeMinValue: props?.data?.rangeMinValue ?? "",
        rangeMaxValue: props?.data?.rangeMaxValue ?? "",
        startDate: props?.data?.startDate ?? new Date(),
        endDate: props?.data?.endDate ?? undefined,
        options: props?.data?.options ?? [],
    };

    let questionnaireQuestion: QuestionDto | undefined = props?.data;
    let title: string = "Edit Question";

    if (
        StringHelper.whenNullOrEmpty(
            questionnaireQuestion?.id,
            Constants.emptyGuid
        ) === Constants.emptyGuid
    ) {
        title = "New Question";
    }

    const onSubmit = (data: QuestionDto) => {
        return Promise.resolve(props?.onSave(data)).then((result) => {
            if (hasClientErrorOperationStatusCode(result)) {
                return;
            }

            props.onHide();
        });
    };

    const isNew = !props?.data?.id || props?.data?.id === Constants.emptyGuid;
    const isLoading = props?.show && !isNew && !props?.data;

    const pollType: boolean = props.questionnaireType === QuestionnaireType.Poll.toLowerCase();
    const feedBackType: boolean = props.questionnaireType === QuestionnaireType.Feedback.toLowerCase();

    if (!props?.show) return null;
    return (
        <Drawer.Form
            className="w-30"
            show={props?.show}
            backdrop={true}
            placement="end"
            scroll={true}
            onHide={props?.onHide}
            title={title}
            initialValues={initialValues}
            validationSchema={validationSchema}
            on_Submit={onSubmit}
        >
            {(formikProps: FormikProps<QuestionDto>) => (
                <>
                    <Form.Text name="name" label="Name" isLoading={isLoading} />
                    <Form.TextArea
                        name="description"
                        rows={3}
                        label="Description"
                        isLoading={isLoading}
                    />
                    {
                        (pollType || feedBackType) && props?.data?.id &&
                        <>
                            <Form.Hidden name="sectionId" value={props.data.sectionId} />
                            <Form.Hidden name="questionOrder" value={props.data.questionOrder} />
                            {!pollType &&
                                <>
                                    <Form.Hidden name="questionTypeId" value={props.data.questionTypeId} />
                                    <Form.Hidden name="rangeMinValue" value={props.data.rangeMinValue} />
                                    <Form.Hidden name="rangeMaxValue" value={props.data.rangeMaxValue} />
                                </>
                            }
                        </>
                    }
                    {!(pollType || feedBackType) && <Form.Dropdown
                        name="sectionId"
                        label="Section"
                        isLoading={isLoading}
                        blankOption={{
                            key: Constants.emptyGuid,
                            display: "Select Section",
                            value: Constants.emptyString,
                        }}
                        options={props?.lookups?.sections}
                    />}
                    {
                        !feedBackType &&
                        <Form.Dropdown
                            name="questionTypeId"
                            label="Question Type"
                            disabled={!isNew}
                            onChange={(e: any) => {
                                const selectedValue = e.target.value;
                                if (
                                    selectedValue !== QuestionTypes.Dropdown.toLowerCase() &&
                                    selectedValue !== QuestionTypes.Checkbox.toLowerCase() &&
                                    selectedValue !== QuestionTypes.Radio.toLowerCase()
                                ) {
                                    formikProps.setFieldValue("options", []);
                                    formikProps.setFieldValue("options", []);
                                }
                            }}
                            isLoading={isLoading}
                            blankOption={{
                                key: Constants.emptyGuid,
                                display: "Select Question Type",
                                value: Constants.emptyString,
                            }}
                            options={props?.lookups?.questionType}
                        />
                    }

                    {formikProps.values.questionTypeId &&
                        formikProps.values.questionTypeId !== null && (
                            <>
                                {formikProps.values.questionTypeId === QuestionTypes.Date.toLowerCase() ? (
                                    <>
                                        <Form.Date
                                            type="date"
                                            label="Min Date"
                                            name={`rangeMinValue`}
                                        />
                                        <Form.Date
                                            type="date"
                                            label="Max Date"
                                            name={`rangeMaxValue`}
                                            useStartDateForMin="rangeMinValue"
                                        />
                                    </>
                                ) : formikProps.values.questionTypeId === QuestionTypes.DateTime.toLowerCase() ? (
                                    <>
                                        <Form.Datetime
                                            label="Min Date Time"
                                            type="datetime-local"
                                            name={`rangeMinValue`}
                                        />
                                        <Form.Datetime
                                            label="Max Date Time"
                                            type="datetime-local"
                                            name={`rangeMaxValue`}
                                        />
                                    </>
                                ) : (
                                    formikProps.values.questionTypeId ===
                                    QuestionTypes.Time.toLowerCase() && (
                                        <>
                                            <Form.Time
                                                label="Min Time"
                                                type="time"
                                                name={`rangeMinValue`}
                                            />
                                            <Form.Time
                                                label="Max Time"
                                                type="time"
                                                name={`rangeMaxValue`}
                                            />
                                        </>
                                    )
                                )}
                            </>
                        )}

                    {!(pollType || feedBackType) && <Form.Number
                        min={1}
                        name="questionOrder"
                        label="Order"
                        isLoading={isLoading}
                    />}

                    <Form.Section title="Activation Dates" />
                    <Form.Date
                        name="startDate"
                        label="Start Date"
                        isLoading={isLoading}
                    />
                    <Form.Date
                        name="endDate"
                        label="End Date"
                        isLoading={isLoading}
                        placeholder="Enter date"
                        useStartDateForMin="startDate"
                    />

                    {formikProps !== null && formikProps.values.questionTypeId &&
                        (formikProps.values.questionTypeId === QuestionTypes.Dropdown.toLowerCase() ||
                            formikProps.values.questionTypeId === QuestionTypes.Checkbox.toLowerCase() ||
                            formikProps.values.questionTypeId === QuestionTypes.Radio.toLowerCase()) && (
                            <QuestionOption formikProps={formikProps} />
                        )}
                </>
            )}
        </Drawer.Form>
    );
};